import React from "react";
import { Lock, Sparkles, Zap, Home, Heart, Briefcase, Coins, Mountain } from "lucide-react";
import { usePlanCheckout } from "../pricing/usePlanCheckout";

const FACET_ICON = { energy: Zap, resonance: Home, love: Heart, career: Briefcase, abundance: Coins, challenges: Mountain };
const FACET_PLAIN = { energy: "Energy", resonance: "Feeling at home", love: "Love & friendship", career: "Work & opportunities", abundance: "Money & luck", challenges: "Growing pains" };

function FacetCard({ f, onUnlock, busy }) {
  const isChallenge = f.key === "challenges";
  const good = isChallenge ? f.value <= 40 : f.value >= 70;
  const meh = isChallenge ? f.value <= 65 : f.value >= 45;
  const barColor = good ? "#C8A96E" : meh ? "#9E8BB0" : "#C9744F";
  const Icon = FACET_ICON[f.key] || Sparkles;
  return (
    <div data-testid={`facet-${f.key}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 hover:border-[#C8A96E]/40 transition-colors">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#A89C98] flex items-center gap-1.5"><Icon size={12} style={{ color: barColor }} /> {FACET_PLAIN[f.key] || f.label}</div>
          <div className="text-[11px] text-white/70 mt-0.5">{f.blurb}</div>
        </div>
        <div className="font-mono-scanner text-3xl leading-none shrink-0" style={{ color: barColor }} data-testid={`facet-${f.key}-value`}>{(f.value / 10).toFixed(1)}<span className="text-sm text-[#A89C98]">/10</span></div>
      </div>
      <div className="mt-3 h-1.5 w-full rounded-full bg-white/[0.06] overflow-hidden">
        <div className="h-full rounded-full transition-[width]" style={{ width: `${f.value}%`, background: barColor }} />
      </div>
      {f.locked ? (
        <button
          type="button"
          onClick={onUnlock}
          disabled={busy}
          data-testid={`facet-${f.key}-locked`}
          className="mt-3 w-full text-left relative rounded-md overflow-hidden group"
        >
          <p className="text-[12px] leading-snug select-none blur-[5px] text-[#E8DFD3]/80" aria-hidden="true">
            Why you got this number and what to do about it in this city - the best timing, simple rituals, and the planet behind it.
          </p>
          <span className="absolute inset-0 flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[#C8A96E] group-hover:text-white transition-colors">
            <Lock size={11} /> Why &amp; how · in your Blueprint
          </span>
        </button>
      ) : (
        <p className="mt-3 text-[12px] text-[#E8DFD3]/90 leading-snug" data-testid={`facet-${f.key}-narrative`}>{f.narrative}</p>
      )}
    </div>
  );
}

function TopFacetTeaser({ top, city, locked, onUnlock, busy }) {
  if (!top) return null;
  return (
    <div className="mb-5 rounded-2xl border border-[#C8A96E]/50 glass-gold p-5 sm:p-6 flex flex-col md:flex-row md:items-center gap-4" data-testid="top-facet-teaser">
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C8A96E] flex items-center gap-1.5"><Sparkles size={11} /> Your standout in {city}</div>
        <div className="font-heading text-2xl sm:text-3xl text-white mt-1" data-testid="top-facet-headline">
          {FACET_PLAIN[top.key] || top.label} <span className="text-[#C8A96E] font-mono-scanner">{top.out_of_10}/10</span>
        </div>
        <p className="text-sm text-[#E8DFD3] mt-1">
          {locked
            ? `${city} boosts your ${(FACET_PLAIN[top.key] || top.label).toLowerCase()} more than anything else. Why - and exactly how to use it there - is in your 21-page Blueprint.`
            : `${city} boosts your ${(FACET_PLAIN[top.key] || top.label).toLowerCase()} more than anything else - the full breakdown is below.`}
        </p>
      </div>
      {locked && (
        <button
          onClick={onUnlock}
          disabled={busy}
          data-testid="top-facet-unlock-btn"
          className="inline-flex items-center justify-center gap-2 h-12 px-6 uppercase tracking-widest text-xs font-bold bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] transition-colors shrink-0"
        >
          <Lock size={13} /> {busy ? "Opening checkout…" : "Unlock the why & how · $4.40"}
        </button>
      )}
    </div>
  );
}

export function FacetBreakdown({ score, facets, topFacet, locked, city }) {
  const { busy, purchase } = usePlanCheckout();
  if (!Array.isArray(facets) || facets.length === 0) return null;
  const unlock = () => purchase("natal_blueprint");
  const isBusy = busy === "natal_blueprint";
  return (
    <section className="mt-10 max-w-4xl mx-auto" data-testid="score-breakdown">
      <TopFacetTeaser top={topFacet} city={city} locked={locked} onUnlock={unlock} busy={isBusy} />
      <div className="flex items-center gap-3 mb-4">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#C8A96E]/40 to-transparent" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C8A96E]">What makes up your {score}</div>
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#C8A96E]/40 to-transparent" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {facets.map((f) => <FacetCard key={f.key} f={f} onUnlock={unlock} busy={isBusy} />)}
      </div>
      <p className="mt-4 text-center text-[10px] uppercase tracking-[0.28em] text-[#A89C98]">
        Your overall score blends all six · feeling at home and energy count the most · growing pains lower it
      </p>
    </section>
  );
}
